import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { fetchTrip, flushQueue, mutate, readQueue, resetTrip, getAccess, ACCESS_KEY } from "./api";
import { isoOf } from "./dates";

const CACHE_KEY = "mbca_trip_cache_v1";
const LOCAL_KEY = "mbca_local_prefs_v1";
const TripCtx = createContext(null);
const UICtx = createContext(null);

const readLocal = () => { try { return JSON.parse(localStorage.getItem(LOCAL_KEY) || "{}"); } catch { return {}; } };
const uid = (p) => `${p}_${Math.random().toString(36).slice(2, 12)}`;

export function TripProvider({ children }) {
  const [trip, setTrip] = useState(() => { try { return JSON.parse(localStorage.getItem(CACHE_KEY)); } catch { return null; } });
  const [loading, setLoading] = useState(!trip);
  const [error, setError] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(readQueue().length);
  const [prefs, setPrefs] = useState(() => ({ organiser: false, demoToday: null, profileId: null, ...readLocal() }));
  const [access, setAccess] = useState(() => !!getAccess());
  useEffect(() => { const h = () => setAccess(false); window.addEventListener("mbca-locked", h); return () => window.removeEventListener("mbca-locked", h); }, []);
  const tripRef = useRef(trip);
  tripRef.current = trip;

  const savePrefs = (patch) => setPrefs((p) => { const n = { ...p, ...patch }; localStorage.setItem(LOCAL_KEY, JSON.stringify(n)); return n; });

  const commit = useCallback((next) => { tripRef.current = next; setTrip(next); localStorage.setItem(CACHE_KEY, JSON.stringify(next)); }, []);

  const refresh = useCallback(async () => {
    try { const data = await fetchTrip(); commit(data); setError(null); } catch (e) { if (!tripRef.current) setError("Couldn't reach the trip server and no offline copy is saved yet."); }
    finally { setLoading(false); }
  }, [commit]);

  useEffect(() => {
    const sync = async () => { setOnline(true); const n = await flushQueue(); setPending(readQueue().length); if (n) await refresh(); };
    const off = () => setOnline(false);
    window.addEventListener("online", sync); window.addEventListener("offline", off);
    sync().then(refresh);
    const iv = setInterval(() => { if (navigator.onLine && getAccess()) refresh(); }, 60000);
    return () => { window.removeEventListener("online", sync); window.removeEventListener("offline", off); clearInterval(iv); };
  }, [refresh, access]);

  const apply = useCallback(async (localUpdate, method, path, body) => {
    commit(localUpdate(tripRef.current));
    try { const r = await mutate(method, path, body); setPending(readQueue().length); if (!r.queued && method !== "delete" && r.data && r.data.id) {
      commit(replaceById(tripRef.current, path.split("/")[1], r.data));
    } } catch (e) { console.error(e); await refresh(); }
  }, [commit, refresh]);

  const profile = useMemo(() => (trip?.profiles || []).find((p) => p.id === prefs.profileId) || null, [trip, prefs.profileId]);
  const stamp = () => ({ createdBy: profile?.name || null, profileId: profile?.id || null });

  const api = useMemo(() => ({
    addEvent: (ev) => { const full = { id: uid("event"), startTime: null, endTime: null, description: "", notes: "", status: "planned", category: "activity", calendarEnabled: true, private: false, source: "user", completed: false, birthdayMoment: false, order: 99, visibility: "everyone", ...stamp(), ownerId: profile?.id || null, ownerName: profile?.name || null, ...ev }; return apply((t) => ({ ...t, events: sortEvents([...t.events, full]) }), "post", "/events", full).then(() => full); },
    updateEvent: (id, patch) => apply((t) => ({ ...t, events: sortEvents(t.events.map((e) => (e.id === id ? { ...e, ...patch } : e))) }), "patch", `/events/${id}`, patch),
    deleteEvent: (id) => apply((t) => ({ ...t, events: t.events.filter((e) => e.id !== id) }), "delete", `/events/${id}`),
    addIdea: (idea) => { const full = { id: uid("idea"), category: "activity", notes: "", flag: null, visibility: "everyone", ...stamp(), ownerId: profile?.id || null, ...idea }; return apply((t) => ({ ...t, ideas: [...t.ideas, full] }), "post", "/ideas", full); },
    updateIdea: (id, patch) => apply((t) => ({ ...t, ideas: t.ideas.map((i) => (i.id === id ? { ...i, ...patch } : i)) }), "patch", `/ideas/${id}`, patch),
    deleteIdea: (id) => apply((t) => ({ ...t, ideas: t.ideas.filter((i) => i.id !== id) }), "delete", `/ideas/${id}`),
    toggleTask: (id) => { const cur = tripRef.current.tasks.find((x) => x.id === id); const patch = { done: !cur.done }; return apply((t) => ({ ...t, tasks: t.tasks.map((x) => (x.id === id ? { ...x, ...patch } : x)) }), "patch", `/tasks/${id}`, patch); },
    addTask: (task) => { const full = { id: uid("task"), done: false, group: "Other", ...task }; return apply((t) => ({ ...t, tasks: [...t.tasks, full] }), "post", "/tasks", full); },
    updateTransport: (id, patch) => apply((t) => ({ ...t, transports: t.transports.map((x) => (x.id === id ? { ...x, ...patch } : x)) }), "patch", `/transports/${id}`, patch),
    updateHotel: (id, patch) => apply((t) => ({ ...t, hotels: t.hotels.map((x) => (x.id === id ? { ...x, ...patch } : x)) }), "patch", `/hotels/${id}`, patch),
    updateSettings: (patch) => apply((t) => ({ ...t, settings: { ...t.settings, ...patch } }), "patch", "/meta/settings", patch),
    updateNotes: (patch) => apply((t) => ({ ...t, notes: { ...t.notes, ...patch } }), "patch", "/meta/notes", patch),
    updateExpenses: (patch) => apply((t) => ({ ...t, expenses: { ...t.expenses, ...patch } }), "patch", "/meta/expenses", patch),
    addSpending: (s) => { const full = { id: uid("spend"), currency: "$", category: "Other", notes: "", city: null, receiptUrl: null, items: [], createdAt: new Date().toISOString(), ...stamp(), ...s }; return apply((t) => ({ ...t, spending: [...(t.spending || []), full] }), "post", "/spending", full).then(() => full); },
    updateSpending: (id, patch) => apply((t) => ({ ...t, spending: (t.spending || []).map((x) => (x.id === id ? { ...x, ...patch } : x)) }), "patch", `/spending/${id}`, patch),
    deleteSpending: (id) => apply((t) => ({ ...t, spending: (t.spending || []).filter((x) => x.id !== id) }), "delete", `/spending/${id}`),
    reset: async () => { const data = await resetTrip(); commit(data); },
    updateProfiles: (profiles) => apply((t) => ({ ...t, profiles }), "patch", "/meta/profiles", { profiles }),
  }), [apply, commit, profile]); // eslint-disable-line

  const today = prefs.demoToday || isoOf(new Date());
  const visible = useMemo(() => {
    if (!trip) return null;
    const mine = (x) => !x.visibility || x.visibility === "everyone" || x.ownerId === prefs.profileId;
    return { ...trip, events: (trip.events || []).filter(mine), ideas: (trip.ideas || []).filter(mine), profiles: trip.profiles || [] };
  }, [trip, prefs.profileId]);

  const value = useMemo(() => ({
    trip: visible, loading, error, online, pending, refresh, today, access, setAccess,
    profile, profiles: trip?.profiles || [], setProfileId: (id) => savePrefs({ profileId: id }),
    lock: () => { localStorage.removeItem(ACCESS_KEY); setAccess(false); },
    organiser: prefs.organiser, setOrganiser: (v) => savePrefs({ organiser: v }),
    demoToday: prefs.demoToday, setDemoToday: (v) => savePrefs({ demoToday: v }),
    ...api,
  }), [visible, trip, loading, error, online, pending, refresh, today, prefs, api, access, profile]);

  return <TripCtx.Provider value={value}>{children}</TripCtx.Provider>;
}

const sortEvents = (evs) => [...evs].sort((a, b) => (a.date + (a.startTime || "99:99") + String(a.order ?? 0).padStart(3, "0")).localeCompare(b.date + (b.startTime || "99:99") + String(b.order ?? 0).padStart(3, "0")));
const replaceById = (t, coll, item) => (t[coll] ? { ...t, [coll]: t[coll].map((x) => (x.id === item.id ? { ...x, ...item } : x)) } : t);

export const useTrip = () => useContext(TripCtx);

export function UIProvider({ children }) {
  const [driver, setDriver] = useState(null);
  const [editor, setEditor] = useState(null);
  const [assistant, setAssistant] = useState({ open: false, prefill: "" });
  const [whatsNext, setWhatsNext] = useState(false);
  const [search, setSearch] = useState(false);
  const [quickAdd, setQuickAdd] = useState(false);
  const [ideaEditor, setIdeaEditor] = useState(null);
  const [expenseEditor, setExpenseEditor] = useState(null);
  const value = useMemo(() => ({
    driver, showDriver: setDriver, closeDriver: () => setDriver(null),
    editor, openEditor: setEditor, closeEditor: () => setEditor(null),
    assistant, openAssistant: (prefill = "") => setAssistant({ open: true, prefill }), closeAssistant: () => setAssistant({ open: false, prefill: "" }),
    whatsNext, setWhatsNext,
    search, openSearch: () => setSearch(true), closeSearch: () => setSearch(false),
    quickAdd, openQuickAdd: () => setQuickAdd(true), closeQuickAdd: () => setQuickAdd(false),
    ideaEditor, openIdeaEditor: (init = {}) => setIdeaEditor(init), closeIdeaEditor: () => setIdeaEditor(null),
    expenseEditor, openExpenseEditor: (init = {}) => setExpenseEditor(init), closeExpenseEditor: () => setExpenseEditor(null),
  }), [driver, editor, assistant, whatsNext, search, quickAdd, ideaEditor, expenseEditor]);
  return <UICtx.Provider value={value}>{children}</UICtx.Provider>;
}
export const useUI = () => useContext(UICtx);
